import { adapter } from '@seek/tealium-adapter';

import {
  getGlobalProperties,
  getPageProperties,
  getLinkProperties,
  getPageEventName,
} from 'src/app/shared/dataAnalytics/reducer';
import { createCypressTracker } from 'src/app/utils/cypress';
import { log } from 'src/app/utils/logging';
import type { Tealium } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

const { initCypressTracker, sendUtagViewEventToCypressTracker } =
  createCypressTracker();

type TrackingAdapterDep = { getTrackingData: () => Tealium };

export const trackingAdapter = ({ getTrackingData }: TrackingAdapterDep) => {
  const tracker = adapter();

  initCypressTracker();

  const aggregateData = (
    trackingData: Tealium,
    eventName?: TrackingEvent['eventName'],
  ) => {
    if (!trackingData.page) {
      return;
    }

    const globalLevelData = getGlobalProperties(trackingData);
    const pageLevelData = getPageProperties(trackingData);
    const linkLevelData = getLinkProperties(trackingData, eventName);
    const newEventData = {
      ...globalLevelData,
      ...pageLevelData,
      ...linkLevelData,
    };

    return newEventData;
  };

  const sendPageEvent = () => {
    const trackingData = getTrackingData();

    const aggregatedData = aggregateData(trackingData);
    const pageName = getPageEventName(trackingData);

    if (!aggregatedData || !pageName) {
      return;
    }
    try {
      tracker.view(pageName, aggregatedData);
    } catch (error) {
      log.error('TrackingAdapter | error sending page view event', {
        pageName,
        aggregatedData,
      });
    }
    sendUtagViewEventToCypressTracker(pageName, aggregatedData);
  };

  const sendLinkEvent = (eventData: TrackingEvent | undefined) => {
    const trackingData = getTrackingData();

    if (eventData) {
      const { eventName } = eventData;
      const aggregatedData = aggregateData(trackingData, eventName);

      const linkData = {
        ...aggregatedData,
        ...eventData,
      };
      try {
        tracker.link(eventName, linkData);
      } catch (error) {
        log.error('TrackingAdapter | error sending link event', {
          eventName,
          linkData,
        });
      }
    }
  };

  return {
    sendLinkEvent,
    sendPageEvent,
  };
};
